import React, { useState, useEffect } from 'react';
import {
    Box,
    FormControl,
    FormLabel,
    Input,
    Textarea,
    Button,
    useToast,
    Text,
    Stack,
    useColorModeValue,
    Flex,
    Spinner,
    FormErrorMessage,
    Image,
    Progress,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Checkbox,
    VStack,
} from '@chakra-ui/react';
import ReactSelect from 'react-select';
import axiosClient, { localUpload } from '../../../axios-client';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { GrAdd } from 'react-icons/gr';
import { FaCloudUploadAlt } from 'react-icons/fa';
import Resizer from "react-image-file-resizer";
import { CloseIcon } from '@chakra-ui/icons';
import { sendToS3 } from '../../../services/s3Upload';
import { renameFile } from '../../../services/renameFile';
import { useStateContext } from '../../../context/ContextProvider';

const FileInput = ({ label, onChange, onOriginalFileChange, value, isRequired, errorMessage, accept, type, width, height }) => {
    const [touched, setTouched] = useState(false);
    const [preview, setPreview] = useState(null);
    const toast = useToast()
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const handleBlur = () => {
        setTouched(true);
    };
    const resizeFile = (file) => {

        if (file.size > 520000)
            return new Promise((resolve) => {
                Resizer.imageFileResizer(
                    file,
                    width,
                    height,
                    "JPEG",
                    90,
                    0,
                    (uri) => {
                        resolve(uri);
                    },
                    "blob"
                );
            });
        else return file;
    };
    const simulateProgress = () => {
        setIsUploading(true);
        setUploadProgress(0);
        const interval = setInterval(() => {
            setUploadProgress(prev => {
                if (prev >= 100) {
                    clearInterval(interval);
                    setIsUploading(false);
                    return 100;
                }
                return prev + 10;
            });
        }, 200);
    };
    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (onOriginalFileChange) {
            onOriginalFileChange({ target: { files: [file] } });
        }
        if (type === "isImage") {
            const fileExtension = file.name.split('.').pop().toLowerCase(); // Get file extension

            // Check if the file is PNG, JPG, or JPEG
            if (!["png", "jpg", "jpeg"].includes(fileExtension)) {
                toast({
                    title: "Type de fichier invalide",
                    description: "Veuillez télécharger une image au format PNG, JPG ou JPEG.",
                    status: "warning",
                    duration: 3000,
                    variant: "left-accent",
                    isClosable: true,
                });

                return; // Block setting the file if it doesn't match the allowed types
            }
        }
        simulateProgress(); // Start progress animation

        if (type === "isImage") {
            try {
                const resizedFile = await resizeFile(file);
                const previewUrl = URL.createObjectURL(resizedFile);
                setPreview(previewUrl);
                const fileName = file.name;
                const imageFile = new File([resizedFile], fileName, { type: resizedFile.type });
                onChange({ target: { files: [imageFile] } });
            } catch (err) {
                // console.error("Error resizing image:", err);
                setIsUploading(false);
                setUploadProgress(0);
            }
        } else if (type === "isVideo") {
            const previewUrl = URL.createObjectURL(file);
            setPreview(previewUrl);
            onChange({ target: { files: [file] } });
        }
    };

    const isInvalid = isRequired && touched && !value;

    return (
        <FormControl isRequired={isRequired} isInvalid={isInvalid} onBlur={handleBlur}>
            <FormLabel>{label}</FormLabel>
            <Flex direction="column" gap={2}>
                <Flex alignItems="center">
                    <Input
                        type="file"
                        display="none"
                        id={`file-input-${label}`}
                        onChange={handleFileChange}
                        accept={accept}
                    />
                    <Button
                        as="label"
                        htmlFor={`file-input-${label}`}
                        leftIcon={<FaCloudUploadAlt />}
                        colorScheme="cyan"
                        variant="outline"
                        borderRadius="md"
                        w="100%"
                        position="relative"
                        overflow="hidden"
                        _hover={{ bgGradient: 'linear(to-r, #29a4a6, #6ad7d9)', color: 'white' }}
                    >
                        <Box position="absolute" left="0" top="0" bottom="0" width={`${uploadProgress}%`}
                            bg="cyan.100" transition="width 0.2s" opacity="0.3" />
                        <Text position="relative">
                            {value ? value.name : 'Choisir un fichier'}
                        </Text>
                    </Button>
                    {preview && (
                        <Box ml={4}>
                            {type === "isImage" ? (
                                <Image src={preview} alt="file preview" boxSize="50px" objectFit="cover" borderRadius="md" />
                            ) : (
                                <video
                                    style={{ borderRadius: "5px" }}
                                    autoPlay
                                    loop
                                    muted
                                    src={preview}
                                    alt="file preview"
                                    width="100px"
                                    height="100px"
                                />
                            )}
                        </Box>
                    )}
                </Flex>
                {isUploading && (
                    <Flex align="center" gap={2}>
                        <Progress
                            value={uploadProgress}
                            size="xs"
                            colorScheme="cyan"
                            borderRadius="full"
                            flex="1"
                        />
                        <Text fontSize="sm" color="gray.500">
                            {uploadProgress}%
                        </Text>
                    </Flex>
                )}
            </Flex>
            {value && (
                <Text mt={2} fontSize="sm" color="gray.500">
                    {value.name}
                </Text>
            )}
            {isInvalid && (
                <FormErrorMessage>{errorMessage || 'Ce champ est requis'}</FormErrorMessage>
            )}
        </FormControl>
    );
};

const AddSerie = () => {
    const { t } = useTranslation()
    const { user } = useStateContext();
    
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [numOfSeasons, setNumOfSeasons] = useState();
    const [releaseDate, setReleaseDate] = useState('');
    const [language, setLanguage] = useState('');
    const [poster, setPoster] = useState(null);
    const [originalPoster, setOriginalPoster] = useState(null);

    const [image, setImage] = useState(null);
    const [originalImage, setOriginalImage] = useState(null);

    const [logo, setLogo] = useState(null);
    const [trailerUrl, setTrailerUrl] = useState(null);
    const [excerptUrl, setExcerptUrl] = useState(null);
    const [videoUrl, setVideoUrl] = useState(null);
    const [filterChunks, setFilterChunks] = useState([]);
    const [selectedPays, setSelectedPays] = useState([]);
    const [pays, setPays] = useState([]);
    const [isAvailableInAll, setIsAvailableInAll] = useState(true);

    const [duration, setDuration] = useState(0);
    const [etat, setEtat] = useState(false);
    const navigate = useNavigate()
    const toast = useToast();
    const [isLoading, setIsLoading] = useState(false)

    const [uploadProgressSubmit, setUploadProgressSubmit] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalProgress, setModalProgress] = useState(0);

    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [filteredSubcategories, setFilteredSubcategories] = useState([]);
    const [selectedSubcategories, setSelectedSubcategories] = useState([]);

    const [minAge, setMinAge] = useState('');
    const [allAgesIncluded, setAllAgesIncluded] = useState(false);

    const [originCountry, setOriginCountry] = useState('');
    const [actors, setActors] = useState([]);
    const [directorName, setDirectorName] = useState('');

    const colorbg = useColorModeValue('white', '#171923')
    const txtcolor = useColorModeValue('black', 'white')
    const colorbghover = useColorModeValue('#e2e8f0', '#1e202b')

    useEffect(() => {
        axiosClient.get('/categories/?page_size=1000&page=1')
            .then((res) => {
                setCategories(res.data.results);
            })
            .catch((err) => {
                toast({
                    title: 'Error fetching categories.',
                    description: err.message,
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            });
        // Fetch subcategories from the API
        axiosClient.get('/subcategories/') // Replace with your API endpoint
            .then((res) => {
                setSubcategories(res.data)
            })
            .catch((err) => {
                toast({
                    title: 'Error fetching subcategories.',
                    description: err.message,
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            });
        axiosClient.get('/pays/?page_size=1000&page=1')
            .then((res) => {
                setPays(res.data.results)
            })
            .catch((err) => {
                toast({
                    title: 'Error fetching countries.',
                    description: err.message,
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            });
    }, []);
    useEffect(() => {
        if (selectedCategories.length === 0) {
            setFilteredSubcategories([]);
            return;
        }

        const selectedCategoryIds = selectedCategories.map(cat => cat.value);
        const filtered = subcategories.filter(subcat =>
            selectedCategoryIds.includes(subcat.category) // Match with category ID
        );

        setFilteredSubcategories(filtered);
    }, [selectedCategories, subcategories]);
    const handleVideoUpload = async (file) => {
        setVideoUrl(file);
        const video = document.createElement('video');
        video.src = URL.createObjectURL(file);

        video.onloadedmetadata = () => {
            setDuration(Math.round(video.duration));
        };
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true)

        let formattedDate = releaseDate;
        if (releaseDate.length === 4) {
            formattedDate = `${releaseDate}-01-01`;
        }
        else {
            toast({
                title: 'Erreur de validation.',
                description: 'Veuillez entrer une année valide',
                status: 'warning',
                duration: 4000,
                isClosable: true,
                position: "bottom-right",
                variant: "left-accent"
            });
            setIsLoading(false)
            setUploadProgressSubmit(0);
            return
        }
        if ((poster?.name && poster?.name.length > 100)
            || (image?.name && image?.name.length > 100)
            || (logo?.name && logo?.name.length > 100)
            || (trailerUrl?.name && trailerUrl?.name.length > 100)
            || (excerptUrl?.name && excerptUrl?.name.length > 100)
        ) {
            toast({
                title: 'Erreur de validation.',
                description: 'Le nom de media ne doit pas dépasser 100 caractères.',
                status: 'warning',
                duration: 4000,
                isClosable: true,
                position: "bottom-right",
                variant: "left-accent"
            });
            setIsLoading(false)
            setUploadProgressSubmit(0);
            return
        }
        setUploadProgressSubmit(0);
        if (!isAvailableInAll && selectedPays.length === 0) {
            toast({
                // title: 'Erreur de validation.',
                description: "Veuillez sélectionner au moins un pays ou cocher la case 'Disponible dans tous les pays'.",
                status: 'warning',
                duration: 3000,
                isClosable: true,
                position: "bottom-right",
                variant: "left-accent"
            });
            setIsLoading(false)
            setUploadProgressSubmit(0);
            return
        }
        const updateProgress = (progress) => {
            setUploadProgressSubmit(progress);
        };

        let trailerUrlS3 = null;
        let excerptUrlS3 = null;
        if (!localUpload) {
            /////////////////////////////////////////////////  S3 UPLOAD START /////////////////////////////////////////////////

            const [trailerUrlS3, excerptUrlS3] = await Promise.all([
                trailerUrl ? sendToS3(trailerUrl, "uploads/videos/trailer", updateProgress) : null,
                excerptUrl ? sendToS3(excerptUrl, "uploads/videos/excerpt", updateProgress) : null
            ]);

            const failedUploads = [];

            if (trailerUrl && !trailerUrlS3) {
                failedUploads.push("bande-annonce");
            }
            if (excerptUrl && !excerptUrlS3) {
                failedUploads.push("extrait");
            }

            if (failedUploads.length > 0) {
                toast({
                    description: `Le téléchargement de ${failedUploads.join(", ")} a échoué.`,
                    status: "warning",
                    duration: null,
                    isClosable: true,
                    position: "bottom-right",
                    variant: "left-accent"
                });

                setIsLoading(false);
                setUploadProgressSubmit(0);
                return;
            }
            /////////////////////////////////////////////////  S3 UPLOAD  END /////////////////////////////////////////////////
        }
        const formData = new FormData();
        formData.append('title', title);
        formData.append('description', description);
        formData.append('releaseDate', formattedDate);
        formData.append('language', language);
        formData.append('totalSeasons', numOfSeasons);
        if (poster) formData.append('poster', renameFile(poster));
        if (originalPoster) formData.append('poster_Hquality', renameFile(originalPoster));

        formData.append('image', renameFile(image));
        formData.append('image_Hquality', renameFile(originalImage))

        if (logo) formData.append('logo', renameFile(logo));


        if (localUpload) {
            //videos on local
            if (trailerUrl) formData.append('trailerUrl', trailerUrl);
            if (excerptUrl) formData.append('excerptUrl', excerptUrl);
        }
        else {
            //videos on S3
            if (trailerUrl) formData.append('trailerUrl', trailerUrlS3);
            if (excerptUrl) formData.append('excerptUrl', excerptUrlS3);
        }

        formData.append('type', "series");
        formData.append('etat', etat);
        if (minAge) formData.append('min_age', minAge);
        if (originCountry) formData.append('origin_country', originCountry);
        if (directorName) formData.append('director_name', directorName);
        if (actors.length > 0) formData.append('actors', JSON.stringify(actors));

        selectedCategories.forEach((category) =>
            formData.append("category", category.value)
        );
        if (selectedSubcategories.length > 0) {
            selectedSubcategories.forEach((subcategory) =>
                formData.append("subcategory", subcategory.value)
            );
        }
        selectedPays.forEach((pays) =>
            formData.append('pays', pays.value)
        );
        formData.append('edited_by', `${user.last_name} ${user.first_name}`);

        try {
            axiosClient.post('/series/create/', formData, {
                onUploadProgress: (progressEvent) => {
                    let percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    if (percentCompleted >= 99) percentCompleted = 99;
                    setUploadProgressSubmit(percentCompleted);
                    setModalProgress(percentCompleted);
                    if (percentCompleted === 99) {
                        setIsModalOpen(true); // Open modal when it reaches 99%
                    }
                }
            })
                .then((response) => {
                    toast({
                        description: 'Serie ajouté avec succès.',
                        status: 'success',
                        duration: 3000,
                        isClosable: true,
                        position: "bottom-right",
                        variant: "left-accent"
                    });
                    navigate('/series')
                    setIsLoading(false)
                    setIsModalOpen(false);
                    setUploadProgressSubmit(0);
                })
                .catch((err) => {
                    const response = err.response;
                    toast({
                        // title: 'Error.',
                        description: "Une erreur s'est produite.",
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                        variant: 'left-accent'
                    });
                    setIsModalOpen(false);
                    setIsLoading(false)
                    setUploadProgressSubmit(0);
                })

        } catch (error) {
            const errorMessage = error.response?.data?.message || "Une erreur s'est produite.";
            toast({
                title: 'Error.',
                description: errorMessage,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            setIsModalOpen(false);
            setIsLoading(false)
            setUploadProgressSubmit(0);
        }
    };


    const isEmpty = () => {
        return !title.trim() || !description.trim() || !releaseDate || !image || !poster || !language || selectedCategories.length === 0 || !numOfSeasons
    };
    const handleAddActor = () => {
        setActors([...actors, '']);
    };

    const handleRemoveActor = (index) => {
        const updatedActors = actors.filter((actor, i) => i !== index);
        setActors(updatedActors);
    };

    const handleActorChange = (index, value) => {
        const updatedActors = actors.map((actor, i) =>
            i === index ? value : actor
        );
        setActors(updatedActors);
    };

    const handleYearChange = (e) => {
        let year = e.target.value;

        // Allow only numeric input and ensure the length doesn't exceed 4 characters
        year = year.replace(/\D/g, '');  // Remove any non-digit characters
        if (year.length <= 4) {
            setReleaseDate(year);
        }
    };
    return (
        <Box>
            <Box w={"100%"} mt={3} mb={2}>
                <Text fontFamily='Plus Jakarta Display' as="cite" fontSize={{ base: "18px", md: "20px", lg: "25px" }} color="gray"><span onClick={() => navigate('/')} style={{ cursor: 'pointer' }} onMouseEnter={e => e.target.style.textDecoration = "underline"} onMouseLeave={e => e.target.style.textDecoration = "none"} >{t('Accueil')}</span>  / <span onClick={() => navigate('/series')} style={{ cursor: 'pointer' }} onMouseEnter={e => e.target.style.textDecoration = "underline"} onMouseLeave={e => e.target.style.textDecoration = "none"} >{t('Liste des séries')}</span> / <span> {t('Ajouter une série')}</span></Text>
            </Box>
            <Flex
                direction={{ base: 'column', md: 'row' }}
                align={{ base: 'stretch', md: 'center' }}
                justify={"center"}
                p={5}
            >

                <Stack bg={useColorModeValue('white', 'gray.700')}
                    rounded={'lg'}
                    p={6}
                    my={5}
                    w={{ base: '100%', md: '100%', lg: '70%', sm: '100%' }}
                >
                    <Box p={5} rounded={'lg'} w="100%" maxW="full" justify={'center'}>
                        <Stack spacing={3} w={{ base: '100%', md: '100%', lg: '100%', sm: '100%' }}>
                            <FormControl isRequired>
                                <FormLabel>Titre</FormLabel>
                                <Input
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="Entrez le titre"
                                />
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel>Description</FormLabel>
                                <Textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Entrez une description"
                                />
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel>Année de sortie</FormLabel>
                                <Input
                                    type="text"
                                    value={releaseDate ? releaseDate.split('-')[0] : ''}
                                    onChange={handleYearChange}
                                    placeholder="Entrez l'année"
                                    maxLength={4}
                                />
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel>Langue</FormLabel>
                                <Input
                                    value={language}
                                    onChange={(e) => setLanguage(e.target.value)}
                                    placeholder="Entrez la langue"
                                />
                            </FormControl>
                            <FormControl isRequired>
                                <FormLabel>Nombre de saisons</FormLabel>
                                <Input
                                    type="text"
                                    value={numOfSeasons}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (/^\d+$/.test(value) || value === "") {
                                            setNumOfSeasons(value);
                                        }
                                    }}
                                    placeholder="Entrez le nombre de saisons"
                                />
                            </FormControl>
                            <FormControl>
                                <Checkbox
                                    py={1}
                                    isChecked={allAgesIncluded}
                                    onChange={(e) => {
                                        setAllAgesIncluded(e.target.checked)
                                        setMinAge("")
                                    }}
                                >

                                    Tous les âges inclus
                                </Checkbox>
                            </FormControl>

                            {!allAgesIncluded && (
                                <FormControl>
                                    <FormLabel>Âge minimum</FormLabel>
                                    <Input
                                        type="text"
                                        value={minAge}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (/^\d+$/.test(value) || value === "") {
                                                setMinAge(value);
                                            }
                                        }}
                                        placeholder="Entrez l'âge minimum"
                                    />
                                </FormControl>
                            )}

                            <FormControl>
                                <FormLabel>Pays d'origine</FormLabel>
                                <Input
                                    value={originCountry}
                                    onChange={(e) => setOriginCountry(e.target.value)}
                                    placeholder="Entrez le pays d'origine"
                                />
                            </FormControl>

                            <FormControl mb={3}>
                                <FormLabel>Acteurs</FormLabel>
                                <VStack spacing={2} align="stretch">
                                    {actors.map((actor, index) => (
                                        <Flex key={index} align="center">
                                            <Input
                                                value={actor}
                                                onChange={(e) => handleActorChange(index, e.target.value)}
                                                placeholder="Nom de l'acteur"
                                            />
                                            <CloseIcon
                                                ml={5}
                                                cursor="pointer"
                                                aria-label="Supprimer acteur"
                                                color="red"
                                                variant="outline"
                                                onClick={() => handleRemoveActor(index)}
                                            />
                                        </Flex>
                                    ))}
                                    <Button
                                        mt={2}
                                        variant={"outline"}
                                        colorScheme="orange"
                                        onClick={handleAddActor}
                                    >
                                        Ajouter un acteur
                                    </Button>
                                </VStack>
                            </FormControl>

                            <FormControl>
                                <FormLabel>Nom du réalisateur</FormLabel>
                                <Input
                                    value={directorName}
                                    onChange={(e) => setDirectorName(e.target.value)}
                                    placeholder="Entrez le nom du réalisateur"
                                />
                            </FormControl>

                            <FileInput
                                width={298}
                                height={498}
                                type={"isImage"}
                                label="Image portrait"
                                value={poster}
                                onChange={(e) => setPoster(e.target.files[0])}
                                onOriginalFileChange={(e) => setOriginalPoster(e.target.files[0])}
                                isRequired
                                errorMessage="Veuillez télécharger l'image portrait."
                                accept=".png, .jpg, .jpeg"
                            />
                            <FileInput
                                width={341}
                                height={192}
                                type={"isImage"}
                                label="Image paysage"
                                value={image}
                                onChange={(e) => setImage(e.target.files[0])}
                                onOriginalFileChange={(e) => setOriginalImage(e.target.files[0])}
                                isRequired
                                errorMessage="Veuillez télécharger Image paysage"
                                accept=".png, .jpg, .jpeg"
                            />
                            <FileInput
                                width={200}
                                height={200}
                                type={"isImage"}
                                label="Logo"
                                value={logo}
                                onChange={(e) => setLogo(e.target.files[0])}
                                errorMessage="Veuillez télécharger un logo."
                                accept=".png, .jpg, .jpeg"
                            />
                            <FileInput
                                type={"isVideo"}
                                label="Bande-annonce"
                                value={trailerUrl}
                                onChange={(e) => setTrailerUrl(e.target.files[0])}
                                errorMessage="Veuillez télécharger une bande-annonce."
                                accept="video/*"
                            />
                            <FileInput
                                type={"isVideo"}
                                label="Extrait"
                                value={excerptUrl}
                                onChange={(e) => setExcerptUrl(e.target.files[0])}
                                errorMessage="Veuillez télécharger un extrait."
                                accept="video/*"
                            />

                            <FormControl isRequired>
                                <FormLabel>Catégories</FormLabel>
                                <ReactSelect
                                    options={categories?.map((cat) => ({
                                        label: cat.name,
                                        value: cat.id,
                                    }))}
                                    value={selectedCategories}
                                    onChange={setSelectedCategories}
                                    isMulti
                                    closeMenuOnSelect={false}
                                    placeholder="Sélectionnez les catégories"
                                    styles={{
                                        container: (base) => ({
                                            ...base,
                                            width: '100%', // Make select take full width of parent (40%)
                                        }),
                                        control: (baseStyles, state) => ({
                                            ...baseStyles,
                                            border: '1px solid #CBD5E0',
                                            backgroundColor: 'transparent',
                                        }),
                                        menu: (provided) => ({
                                            ...provided,
                                            textAlign: 'left',
                                            backgroundColor: colorbg,
                                            cursor: "pointer"
                                        }),

                                        option: (provided, state) => ({
                                            ...provided,
                                            color: txtcolor,
                                            backgroundColor: state.isSelected ? colorbg : 'inherit',
                                            '&:hover': {
                                                backgroundColor: colorbghover,
                                            },
                                            cursor: "pointer"
                                        }),
                                        singleValue: (provided) => ({
                                            ...provided,
                                            color: txtcolor
                                        }),



                                    }}
                                />
                            </FormControl>

                            <FormControl >
                                <FormLabel>Sous-catégories</FormLabel>
                                <ReactSelect
                                    options={filteredSubcategories.map((subcat) => ({
                                        label: subcat.name,
                                        value: subcat.id,
                                    }))}
                                    value={selectedSubcategories}
                                    onChange={setSelectedSubcategories}
                                    isMulti
                                    closeMenuOnSelect={false}
                                    placeholder="Sélectionnez les sous-catégories"
                                    isDisabled={selectedCategories.length === 0} // Disable if no category selected
                                    styles={{
                                        container: (base) => ({
                                            ...base,
                                            width: '100%', // Make select take full width of parent (40%)
                                        }),
                                        control: (baseStyles, state) => ({
                                            ...baseStyles,
                                            border: '1px solid #CBD5E0',
                                            backgroundColor: 'transparent',
                                        }),
                                        menu: (provided) => ({
                                            ...provided,
                                            textAlign: 'left',
                                            backgroundColor: colorbg,
                                            cursor: "pointer"
                                        }),

                                        option: (provided, state) => ({
                                            ...provided,
                                            color: txtcolor,
                                            backgroundColor: state.isSelected ? colorbg : 'inherit',
                                            '&:hover': {
                                                backgroundColor: colorbghover,
                                            },
                                            cursor: "pointer"
                                        }),
                                        singleValue: (provided) => ({
                                            ...provided,
                                            color: txtcolor
                                        }),



                                    }}
                                />
                            </FormControl>
                            <FormControl >
                                <Checkbox
                                    my={2}
                                    isChecked={isAvailableInAll}
                                    onChange={(e) => {
                                        setSelectedPays([])
                                        setIsAvailableInAll(e.target.checked)
                                    }}
                                >
                                    Disponible dans tous les pays
                                </Checkbox>
                                {!isAvailableInAll && (
                                    <>
                                        <FormLabel>Pays</FormLabel>
                                        <ReactSelect
                                            options={pays.map((pays) => ({
                                                label: pays.name,
                                                value: pays.id,
                                            }))}
                                            value={selectedPays}
                                            onChange={setSelectedPays}
                                            isMulti
                                            placeholder="Sélectionnez les pays"
                                        /></>)}
                            </FormControl>
                            <FormControl>
                                <Checkbox
                                    isChecked={etat}
                                    onChange={(e) => setEtat(e.target.checked)}
                                >
                                    Activer le contenu
                                </Checkbox>
                            </FormControl>

                            <Stack direction={['column', 'row']} spacing={10} pt={2} justifyContent="end">
                                {isEmpty() ? (
                                    <Button
                                        type="submit"
                                        onClick={handleSubmit}
                                        colorScheme="green"
                                        isDisabled
                                        leftIcon={<GrAdd />}
                                    >
                                        Ajouter
                                    </Button>
                                ) : (
                                    <Button
                                        type="submit"
                                        onClick={handleSubmit}
                                        colorScheme="green"
                                        isDisabled={isLoading}
                                        leftIcon={!isLoading && <GrAdd />}
                                    >
                                        {(isLoading && !isModalOpen) ?
                                            <Flex align="center" gap={2}>
                                                <Spinner size="sm" />
                                                <Text>Chargement en cours... {uploadProgressSubmit}%</Text>
                                            </Flex> : "Ajouter"}
                                    </Button>
                                )}
                            </Stack>
                        </Stack>
                    </Box>
                    <Modal size={"lg"} closeOnOverlayClick={false} isOpen={isModalOpen} isCentered>
                        <ModalOverlay />
                        <ModalContent>
                            <ModalHeader>Chargement en cours</ModalHeader>
                            {/* <ModalCloseButton /> */}
                            <ModalBody>
                                <Text mb={5}>Veuillez patienter pendant que nous préparons vos fichiers.</Text>
                                <Progress value={modalProgress} size="md" colorScheme="teal" isIndeterminate={modalProgress < 100} />
                                {/* <Text mt={2} textAlign="center">{modalProgress}%</Text> */}
                            </ModalBody>
                            <ModalFooter />
                        </ModalContent>
                    </Modal>
                </Stack>
            </Flex>
        </Box>
    );
};

export default AddSerie;
